// History-API-basiertes Client-Routing (kein Framework)
// Routen werden über window.location.pathname gesteuert: /dashboard, /login, etc.

export type RouteHandler = () => void | Promise<void>;

export interface Route {
  path: string;
  handler: RouteHandler;
  // Seiten die nur für eingeloggte Spieler zugänglich sind
  requiresAuth: boolean;
  // Optional: statt Login-Redirect eine öffentliche Variante für Gäste rendern (NT-STREAM-FIX-01)
  guestHandler?: RouteHandler | undefined;
}

const routes: Route[] = [];

// Aktuelle Route-Parameter (z.B. { slug: 'xsymme' } für /community/:slug)
let currentParams: Record<string, string> = {};

/** Liefert die Parameter der aktuellen Route */
export function getRouteParams(): Record<string, string> {
  return currentParams;
}

/** Prüft ob ein registrierter Pfad mit dem aktuellen Pfad matcht (unterstützt :param-Segmente) */
function matchRoute(registeredPath: string, currentPath: string): Record<string, string> | null {
  const regParts = registeredPath.split('/');
  const curParts = currentPath.split('/');
  if (regParts.length !== curParts.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < regParts.length; i++) {
    const reg = regParts[i] ?? '';
    const cur = curParts[i] ?? '';
    if (reg.startsWith(':')) {
      params[reg.slice(1)] = cur;
    } else if (reg !== cur) {
      return null;
    }
  }
  return params;
}

// Route-Change-Listener (z.B. für Bottom-Nav)
const routeChangeListeners: ((path: string) => void)[] = [];

/** Callback registrieren – wird nach jedem erfolgreichen Route-Wechsel aufgerufen */
export function onRouteChange(callback: (path: string) => void): void {
  routeChangeListeners.push(callback);
}

function notifyRouteChange(path: string): void {
  for (const cb of routeChangeListeners) cb(path);
}

let notFoundHandler: RouteHandler = (): void => {
  const app = document.getElementById('app');
  if (app) {
    app.innerHTML = '<p style="padding:2rem;color:var(--accent-red)">404 – SEITE NICHT GEFUNDEN</p>';
  }
};

// Route registrieren
export function route(
  path: string,
  handler: RouteHandler,
  requiresAuth = true,
  guestHandler?: RouteHandler,
): void {
  routes.push({ path, handler, requiresAuth, guestHandler });
}

// Fallback-Handler für unbekannte Routen
export function notFound(handler: RouteHandler): void {
  notFoundHandler = handler;
}

// Aktuelle Route aus pathname lesen (ohne Query-String, der liegt in window.location.search)
function getCurrentPath(): string {
  return window.location.pathname;
}

// Route auflösen und Handler aufrufen
async function resolve(): Promise<void> {
  const path = getCurrentPath();

  // API-Routen nicht vom Frontend-Router abfangen
  if (path.startsWith('/api/')) return;

  // Exakte Übereinstimmung suchen
  let matched = routes.find((r) => r.path === path);
  let params: Record<string, string> = {};

  // Falls keine exakte Übereinstimmung: parametrisierte Routen prüfen
  if (!matched) {
    for (const r of routes) {
      if (!r.path.includes(':')) continue;
      const p = matchRoute(r.path, path);
      if (p !== null) {
        matched = r;
        params = p;
        break;
      }
    }
  }

  currentParams = params;

  if (!matched) {
    await Promise.resolve(notFoundHandler());
    notifyRouteChange(path);
    return;
  }

    // Auth-Guard: bei nicht eingeloggten Spielern → /login
  if (matched.requiresAuth) {
    const { gameState } = await import('./state/gameState.js');
    if (!gameState.isAuthenticated()) {
      if (matched.guestHandler) {
        await Promise.resolve(matched.guestHandler());
        notifyRouteChange(getCurrentPath());
        return;
      }
      navigate('/login');
      return;
    }

    // Warteliste-Guard (NT-ALPHA-REWORK-01): Spieler auf der Alpha-Warteliste haben keinen
    // Spielzugang – alle geschützten Routen (außer /warteliste selbst) leiten dorthin um.
    if (gameState.isWaitlisted() && path !== '/warteliste') {
      navigate('/warteliste');
      return;
    }

    // Jail-Guard: gesperrte Routen für Spieler im Knast → /jail
    const JAIL_BLOCKED_PATHS = [
      '/dashboard',
      '/jobs', '/talentbaum',
      '/jobs/pfand-betrieb', '/jobs/werkstatt', '/jobs/werkstatt/craft',
      '/labor', '/gewaechshaus', '/rolltisch', '/viertel',
      '/business/crime-ops', '/business/lagerhaus', '/business/markt', '/business/boerse',
      '/immobilien/geschaefte', '/immobilien/lager', '/immobilien/verstecke',
      '/community/leaderboard', '/community/stream', '/community/marktplatz',
    ];
    if (JAIL_BLOCKED_PATHS.includes(path)) {
      const player = gameState.getPlayer();
      if (player?.isInJail) {
        navigate('/jail');
        return;
      }
    }
  }

  await Promise.resolve(matched.handler());
  notifyRouteChange(getCurrentPath());
}

// Router initialisieren – auf popstate hören (Browser Vor/Zurück)
export function initRouter(): void {
  window.addEventListener('popstate', () => void resolve());

  // Klicks auf interne Links abfangen – SPA-Navigation ohne Seiten-Reload
  document.addEventListener('click', (e: MouseEvent) => {
    const target = (e.target as Element).closest('a');
    if (!target) return;
    const href = target.getAttribute('href');
    if (!href) return;
    // Nur absolute Pfade auf demselben Origin abfangen, keine externen Links
    if (!href.startsWith('/') || href.startsWith('//')) return;
    // Backend-Routen dem Browser überlassen (kein SPA-Routing)
    if (href.startsWith('/api/')) return;
    if (href.startsWith('/dashboard/auth/')) return;
    e.preventDefault();
    navigate(href);
  });

  // Initiale Route beim Laden auflösen
  void resolve();
}

// Programmatisch navigieren
export function navigate(path: string): void {
  history.pushState({}, '', path);
  void resolve();
}
